import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import {
	getUsersFromCollaborationGraph,
	getUsersWithProductAccess,
	type CrossJoinIntegraton,
} from '@atlassian/cross-join-nudges';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';

import { showNudgeFlag } from '../ui/crossJoinNudgesFlags';

import {
	fireExperimentError,
	fireSiteOptedOutAnalytics,
	getExperimentAnalytics,
} from './analytics';
import { resolveCrossJoinNudges } from './resolve-cross-join-nudges';
import {
	getFeatureVisitedCount,
	increaseFeatureVisitedCount,
	isFeatureVisited,
	isUserOptedOut,
} from './feature-visited';
import { setHasSeenNudgeInLast7Days } from './caching';
import { resolveJpdCrossJoinNudges } from './resolve-jpd-cross-join-nudges';

export interface CrossJoinNudgesInput {
	cloudId: string;
	userId: string | null;
	flags: FlagsStateContainer;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	push: (url: string, forceReload?: boolean | undefined) => void;
}

const MAX_FEATURE_VISIT_COUNT = 3;

// NOTE: This file is for old nudges - setup new files specifically per nudge experiment and add all helper functions there to avoid conflict
export const triggerJiraCrossJoinNudge = async ({
	cloudId,
	userId,
	flags,
	createAnalyticsEvent,
	push,
}: CrossJoinNudgesInput) => {
	const integration: CrossJoinIntegraton = 'crossJoinNudgesV3';
	const analytics = getExperimentAnalytics(integration, createAnalyticsEvent);

	try {
		if (!userId || (await isFeatureVisited(integration))) {
			return;
		}

		const response = await resolveCrossJoinNudges({ userId, cloudId });
		if (response.ineligibilityReason === null) {
			if (
				isUserOptedOut(integration) ||
				getFeatureVisitedCount(integration) >= MAX_FEATURE_VISIT_COUNT
			)
				return;
			await showNudgeFlag({
				product: 'jira-software',
				integration,
				userId,
				cloudId,
				collaborators: response.collaborators,
				createAnalyticsEvent,
				flags,
				pushRoute: push,
			});
			increaseFeatureVisitedCount(integration);

			// this is used in the eligibility check for loom nudge
			await setHasSeenNudgeInLast7Days();
		} else if (response.ineligibilityReason === 'SITE_OPTED_OUT') {
			fireSiteOptedOutAnalytics(analytics);
		}
	} catch (error: any) {
		fireExperimentError(analytics, error);
	}
};

type CrossJoinNudgesEligibilityInput = Pick<
	CrossJoinNudgesInput,
	'cloudId' | 'userId' | 'createAnalyticsEvent'
>;

export const checkJpdCrossJoinNudgeEligibility = async ({
	cloudId,
	userId,
	createAnalyticsEvent,
}: CrossJoinNudgesEligibilityInput): Promise<boolean> => {
	const integration: CrossJoinIntegraton = 'crossJoinNudgesConfluenceToJpd';
	const analytics = getExperimentAnalytics(integration, createAnalyticsEvent);

	try {
		if (!userId) {
			return false;
		}
		if (await isFeatureVisited('crossJoinNudgesConfluenceToJpd')) {
			return false;
		}

		const eligibilityResponse = await resolveJpdCrossJoinNudges(cloudId);

		if (!eligibilityResponse.eligible) {
			if (eligibilityResponse.ineligibilityReason === 'SITE_OPTED_OUT') {
				fireSiteOptedOutAnalytics(analytics);
			}
			return false;
		}
		return true;
	} catch (error: any) {
		fireExperimentError(analytics, error);
		return false;
	}
};

export const showJpdCrossJoinNudge = async ({
	cloudId,
	userId,
	flags,
	createAnalyticsEvent,
	push,
}: CrossJoinNudgesInput) => {
	const integration: CrossJoinIntegraton = 'crossJoinNudgesConfluenceToJpd';
	const analytics = getExperimentAnalytics(integration, createAnalyticsEvent);

	try {
		if (!userId) {
			return;
		}

		const collaborators = await getUsersFromCollaborationGraph(integration, cloudId, userId);
		const collaboratorsOnJpd = await getUsersWithProductAccess(
			'jira-product-discovery',
			cloudId,
			collaborators,
		);

		if (
			isUserOptedOut(integration) ||
			getFeatureVisitedCount(integration) >= MAX_FEATURE_VISIT_COUNT
		)
			return;
		await showNudgeFlag({
			product: 'jira-product-discovery',
			integration,
			userId,
			cloudId,
			collaborators: collaboratorsOnJpd,
			createAnalyticsEvent,
			flags,
			pushRoute: push,
		});
		increaseFeatureVisitedCount(integration);

		// this is used in the eligibility check for loom nudge
		await setHasSeenNudgeInLast7Days();
	} catch (error: any) {
		fireExperimentError(analytics, error);
	}
};
